
import {
  useContext, useCallback, useMemo,
  useEffect,
  useState,
} from 'preact/hooks';
import { Fragment } from 'preact';
import PropTypes from 'prop-types';

import StoreContext from '../helpers/context';
import {
  makeAnsweringServiceSignupRequest,
  makeFreeSignupRequest,
  makeFreeTrialSignupRequest,
  sendSignupEvent,
} from '../helpers/fetch-helper';
import {
  getSanitizedErrorMessage,
  getSignupRedirectUrl, getUserIdentifierForAnalytics, redirectToErrorPage, redirect,
  getUniqueUserIdFromQueryParam,
} from '../helpers/common-helper';
import { getPlanById } from '../helpers/plans/plans-common-helper';
import {
  updateAgreedToTerms, updateContact,
  updateLoader, updateModalsView, updateProvince,
  updateRecaptcha,
} from '../store/dispatch-actions';
import {
  trackFailure,
  trackSuccess,
  ANALYTICS_LITERAL,
  pushCustomEventToDataLayer,
  analyticsSignupTypeMap,
} from '../helpers/analytics';
import PaymentFailedModal from '../modals/payment-failed';

import TermsSection from './terms-section';
import getSessionInfo from '../helpers/session-Info';
import {
  getReCaptchaTokenAndExecuteCallback,
  RECAPTCHA_ACTIONS,
  initializeCheckboxRecaptcha,
  getRecaptchaCheckboxToken,
  isRecaptchaCheckBoxChecked,
  RECAPTCHA_TYPE,
} from '../helpers/recaptcha';
import {
  EMAIL_API_STATUS,
  EMAIL_AVAILABILITY_STATUS,
  FAILURE_MESSAGE_BY_EVENT,
  FORM_ERROR_MESSAGES,
  PROVINCE_LITERAL,
  SIGNUP_EVENTS,
  SIGNUP_TYPES,
} from '../utils/common-literals';
import {
  isOnboardingRequired, isAnsweringServiceBrand, isCanadaBrand,
  isSetmoreStartNowPage,
  isSetmoreBrand,
  isEmailValidityCheckEnabledBrand,
  isServiceForgeBrand,
} from '../helpers/brands';
import RecaptchaCheckBox from '../recaptcha/RecaptchaCheckBox';
import { checkEmailAvailability, validateContactFields } from '../helpers/contact-helper';


const { brand: { websiteUrl }, leadInfo = {} } = getSessionInfo();

export default function FreeSignupSection({ signupType }) {
  const { state, dispatch } = useContext(StoreContext);

  const [isSubmitEventInProgress, setIsSubmitEventInProgress] = useState(false);

  const resetModalView = useCallback(() => {
    dispatch(updateModalsView({ }));
  });

  const analyticsPayload = useMemo(() => {
    if (signupType === SIGNUP_TYPES.NO_CARD_SIGNUP || isAnsweringServiceBrand) {
      const selectedPlan = getPlanById(state.planDetails?.selectedPlanId);
      return {
        planInfo: {
          price: selectedPlan?.price,
          id: selectedPlan?.id,
          description: selectedPlan?.description,
          planPeriod: selectedPlan?.planPeriod,
        },
        amount: 0,
        type: isAnsweringServiceBrand ? 'CC Disabled' : 'Free Trial',
      };
    }
    return {
      planInfo: {
        price: 0,
        id: isSetmoreBrand ? '0000' : 'freeSignup',
        description: 'Free',
        planPeriod: isSetmoreBrand ? 'Monthly' : 'na',
      },
      amount: 0,
      type: 'Free signup',
    };
  }, [state.planDetails]);

  const failureCallBack = useCallback((response) => {
    setIsSubmitEventInProgress(false);
    const sanitizedErrorMessage = getSanitizedErrorMessage(response);

    if (
      signupType !== SIGNUP_TYPES.PAID_SIGNUP
      && response?.message === SIGNUP_EVENTS.EMAIL_ADDRESS_UNAVAILABLE
    ) {
      dispatch(
        updateContact({
          field: 'email',
          value: {
            ...state?.contact.email,
            valid: false,
            lookupCache: [
              ...(state?.contact?.email?.lookupCache || []).filter(
                lookup => lookup.email !== state?.contact.email.value,
              ),
              { email: state?.contact.email.value, status: EMAIL_AVAILABILITY_STATUS.UNAVAILABLE },
            ],
            errorMsg: FORM_ERROR_MESSAGES.email.alreadyExists,
          },
        }),
      );

      dispatch(updateLoader({ show: false }));
      return;
    }

    if (isSetmoreStartNowPage()) {
      pushCustomEventToDataLayer({
        event: 'signup-email-fail',
        data: {
          errormsg: sanitizedErrorMessage.analyticsMessage,
          timestamp: new Date().getTime(),
        },
      });
    }

    if (response?.message?.startsWith('Recaptcha score')
      && !state?.recaptcha?.invisibleCaptchaFailed) {
      if (!state?.recaptcha?.display) {
        dispatch(
          updateRecaptcha({
            data: { checkBoxChecked: false, display: true, invisibleCaptchaFailed: true },
          }),
        );
        initializeCheckboxRecaptcha(RECAPTCHA_ACTIONS.FREE_SIGNUP);
        sendSignupEvent({
          signupType: analyticsSignupTypeMap[signupType],
          status: 'Intermediate',
          rVersion: RECAPTCHA_TYPE.INVISIBLE,
          email: state?.contact?.email?.value || leadInfo?.email,
          accountId: getUniqueUserIdFromQueryParam(),
          failureReason: getSanitizedErrorMessage(response).analyticsMessage,
          comments: 'Submit event',
        });
      }
      dispatch(updateLoader({ show: false }));
      return;
    }

    const email = state?.contact?.email?.value || leadInfo?.email;

    if (response?.message) {
      trackFailure({
        planInfo: analyticsPayload?.planInfo,
        amount: analyticsPayload?.amount,
        email,
        collatedUID: getUserIdentifierForAnalytics({ email }),
        message: sanitizedErrorMessage.analyticsMessage,
        userFriendlyErrorMessage: response?.message?.startsWith('Recaptcha')
          ? ''
          : sanitizedErrorMessage.errorMessage,
        numberOfUsers: 0,
        customerType: ANALYTICS_LITERAL.NEW_CUSTOMER,
        type: analyticsPayload?.type,
      });
    }

    if (response?.message?.startsWith('Recaptcha')) {
      redirectToErrorPage();
    } else {
      dispatch(
        updateModalsView({
          view: 'failure',
          message: sanitizedErrorMessage.errorMessage,
        }),
      );
    }

    dispatch(updateLoader({ show: false }));
  });

  const successCallBack = (response) => {
    setIsSubmitEventInProgress(false);
    trackSuccess({
      amount: analyticsPayload?.amount,
      ...state,
      ...response,
      accountId: response?.accountId,
      planInfo: analyticsPayload?.planInfo,
      type: analyticsPayload?.type,
      customerType: ANALYTICS_LITERAL.NEW_CUSTOMER,
    });

    if (isSetmoreStartNowPage()) {
      pushCustomEventToDataLayer({
        event: 'signup-email-success',
        data: { timestamp: new Date().getTime() },
      });
    }

    if (isOnboardingRequired || isServiceForgeBrand) {
      redirect(response.redirectUrl);
    } else {
      redirect(getSignupRedirectUrl(response));
    }
  };

  const processSignup = (captchaToken) => {
    const requestProps = {
      payload: { ...state, captchaToken },
      successCallBack,
      failureCallBack,
    };

    if (isAnsweringServiceBrand) {
      makeAnsweringServiceSignupRequest(requestProps);
    } else if (signupType === SIGNUP_TYPES.NO_CARD_SIGNUP) {
      makeFreeTrialSignupRequest(requestProps);
    } else {
      makeFreeSignupRequest(requestProps);
    }
  };


  const validateRequiredFields = () => {
    let contact = state?.contact;

    contact = validateContactFields(contact, dispatch);

    const isAgreed = state.agreedToTerms.agreed;
    const contactFieldsValid = Object.values(contact).every(({ valid }) => valid);
    const isValidProvince = isCanadaBrand ? state.province?.valid : true;

    if (isAgreed && contactFieldsValid && isValidProvince) {
      return true;
    }

    if (!contactFieldsValid) {
      const field = Object.keys(contact).find(key => !contact[key].valid);
      if (!isSetmoreStartNowPage()) {
        document.getElementById(field).scrollIntoView({ behavior: 'smooth' });
      }
      dispatch(updateContact({ field, value: { ...contact[field], valid: false } }));
      return false;
    }

    if (isCanadaBrand && !state?.province?.valid) {
      document.getElementById(PROVINCE_LITERAL).scrollIntoView({ behavior: 'smooth' });
      dispatch(updateProvince({ ...state.province, valid: false }));
      return false;
    }

    if (!isAgreed) {
      dispatch(updateAgreedToTerms({ showToolTip: true, agreed: state.agreedToTerms.agreed }));
      return false;
    }
    return false;
  };


  const onClickOfSignup = () => {
    if (isSubmitEventInProgress) return;

    if (
      isEmailValidityCheckEnabledBrand
      && state?.contact?.email?.availabilityCheckApiStatus
        === EMAIL_API_STATUS.INPROGRESS
    ) {
      dispatch(updateLoader({ show: true }));
      return;
    }

    if (state.modalToBeShown.view) {
      resetModalView();
    }
    if (!validateRequiredFields()) return;

    const shouldCheckEmailAvailability = state?.contact.email.lookupCache?.findIndex(
      lookup => lookup.email === state?.contact.email.value,
    ) === -1;

    if (state?.recaptcha?.display) {
      const checkboxRecaptchaToken = getRecaptchaCheckboxToken();
      if (isRecaptchaCheckBoxChecked(checkboxRecaptchaToken)) {
        dispatch(updateLoader({ show: true }));
        dispatch(
          updateRecaptcha({ data: { checkBoxChecked: true, display: false } }),
        );
        if (shouldCheckEmailAvailability) {
          checkEmailAvailability({
            value: state?.contact.email.value,
            contact: state?.contact,
            dispatch,
            recaptcha: state?.recaptcha,
            signupType,
          });
          return;
        }
        setIsSubmitEventInProgress(true);
        processSignup(checkboxRecaptchaToken);
      } else {
        dispatch(updateRecaptcha({ data: { checkBoxChecked: false } }));
      }
    } else {
      dispatch(updateLoader({ show: true }));
      if (shouldCheckEmailAvailability) {
        checkEmailAvailability({
          value: state?.contact.email.value,
          contact: state?.contact,
          dispatch,
          recaptcha: state?.recaptcha,
          signupType,
        });
        return;
      }
      setIsSubmitEventInProgress(true);
      getReCaptchaTokenAndExecuteCallback(
        RECAPTCHA_ACTIONS.FREE_SIGNUP,
        processSignup,
      );
    }
  };

  const onCancel = useCallback(() => {
    redirect(websiteUrl);
  });

  useEffect(() => {
    if (
      isEmailValidityCheckEnabledBrand
      && state?.contact?.email?.availabilityCheckApiStatus
        === EMAIL_API_STATUS.COMPLETED
      && state.loader
    ) {
      onClickOfSignup();
    }
  }, [state.contact?.email?.availabilityCheckApiStatus, state.loader]);

  return (
    <Fragment>
      <RecaptchaCheckBox />
      {isSetmoreStartNowPage() ? (
        <button
          data-testid="submit"
          data-click-id="submit"
          onClick={onClickOfSignup}
          tabIndex={4}
          type='submit'
          className={`${
            state.loader ? 'g-btn-processing' : ''
          } mb-2 btn btn-social btn-social-primary fx-vc`}
        >
          <span className="awd-btn-label">Sign up</span>
        </button>
      ) : (
        <>
          <TermsSection
            onCancel={onCancel}
            onSubmit={onClickOfSignup}
            btnText="Sign up"
          />
        </>
      )}
      {state.modalToBeShown.view === 'failure' && (
        <PaymentFailedModal
          headerText={FAILURE_MESSAGE_BY_EVENT.SIGNUP.ERROR_HEADER}
          message={state.modalToBeShown.message}
          onClose={resetModalView}
          tryAgain={onClickOfSignup}
        />
      )}
    </Fragment>
  );
}

FreeSignupSection.propTypes = {
  signupType: PropTypes.string,
};

FreeSignupSection.defaultProps = {
  signupType: SIGNUP_TYPES.FREE_SIGNUP,
};
